/// <amd-module name="Wit/WorkItemForm/Header/WorkItemFormHeader" />
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { ClipboardButton, copyToClipboard } from "VSSUI/Clipboard";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FocusOrMouseWithin, IFocusOrMouseWithinStatus } from "VSSUI/FocusOrMouseWithin";
import { Icon } from "VSSUI/Icon";
import { IIdentity, IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { Control } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, DalFields } from "Wit/Common/Generated/Constants";
import { getAllowedIdentityTypes } from "Wit/Common/Utilities/Identity";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import {
    convertIIdentityToWorkItemIdentityRef,
    convertPotentialIdentityRefFromFieldValue,
    convertWorkItemIdentityRefToIIdentity,
    getDistinctDisplayName
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { WorkItemTypeIconProvider } from "Wit/UI/ComponentProviders";
import { WorkItemTypeIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { IWorkItemTrackingDataProvider, WorkItemTrackingDataProvider } from "Wit/UI/DataProviders";
import { IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemFormPageContext, WorkItemFormPageContext } from "Wit/WorkItemForm/Common/Hooks";
import { WorkItemFormHeaderCommandBar } from "Wit/WorkItemForm/Header/WorkItemFormCommandBar";
import { WorkItemTagPicker } from "Wit/WorkItemForm/Header/WorkItemTagPicker";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IWorkItem, IWorkItemBulkSaveResult, IWorkItemInfoText } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { isReadOnly } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import * as React from "react";
import { WorkItemFieldSubscriber } from "../Common/Components/WorkItemFieldSubscriber";
import { getWorkItemFormTitle, isNewWorkItemFormRoute } from "../Common/RoutingUtils";
import { IWorkItemFormCommentService } from "../Common/Services";

const WifDialogFullScreenSettingKey = "WorkItemForm/WifDialog/FullScreen";

export interface IWorkItemFormHeaderProps {
    onCommentCountClick: () => void;
    onDelete?: () => void;
    onDismiss?: () => void;
    onRefreshed: () => void;
    onReset: () => void;
    onSave: (callback: (result: IWorkItemBulkSaveResult[]) => void, errorCallback?: ((error: any) => void) | undefined) => void;
    onToggleFullscreen?: () => void;
    systemControls: Control[];
    shortcutGroupName: string;
    currentIterationPath?: string;
}

export class WorkItemFormHeader extends React.Component<IWorkItemFormHeaderProps> {
    public static contextType = WorkItemFormPageContext;
    private pageContext: IVssPageContext;

    private workItemFormCommentService: IWorkItemFormCommentService;

    private identityPickerProvider: IPeoplePickerProvider;
    private workItemTrackingDataProvider: IWorkItemTrackingDataProvider;

    private workItemAssignment: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
    private assignedIdentityResolved = new ObservableValue<boolean>(false);
    private workItemTitle: IObservableValue<string>;
    private commentCount: IObservableValue<number>;
    private workItemType: IObservableValue<IWorkItemTypeData>;
    private isDialogFullscreen: IObservableValue<boolean>;
    private workItem: IWorkItem;
    private workItemEventService: IWorkItemEventService;
    private workItemFieldService: IWorkItemFieldService | IWorkItemFieldService2;

    private titlePlaceholder = Resources.EnterTitle;

    constructor(props: IWorkItemFormHeaderProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this.pageContext = context.pageContext;

        this.workItem = context.workItem;

        this.workItemTitle = new ObservableValue<string>(this.workItem.getFieldValueById(DalFields.TitleId));
        this.commentCount = new ObservableValue(this.workItem.getFieldValueById(DalFields.CommentCountId));
        this.workItemType = new ObservableValue(this.workItem.getTypeData());

        this.workItemFieldService = isFeatureFlagEnabled(context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
                    ? context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
                    : context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        this.identityPickerProvider = new PeoplePickerProvider(this.pageContext, {
            identityTypes: getAllowedIdentityTypes(this.pageContext, { excludeGroups: true })
        });

        const workItemAssignmentRef: IdentityRef = this.workItem.getFieldValueById(DalFields.AssignedToId, false, false, true);
        this.updateIdentityPicker(workItemAssignmentRef);

        this.workItemTrackingDataProvider = new WorkItemTrackingDataProvider(context.pageContext);
        this.workItemEventService = context.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        this.workItemFormCommentService = context.pageContext.getService<IWorkItemFormCommentService>("IWorkItemFormCommentService");

        if (props.onDismiss) {
            this.isDialogFullscreen = new ObservableValue<boolean>(
                this.pageContext
                    .getService<ISettingsService>("ISettingsService")
                    .getEntry<boolean>(WifDialogFullScreenSettingKey, SettingsUserScope.Me) ?? false
            );
        }

        const titleControlInfo = this.props.systemControls.find(control => control.id === CoreFieldRefNames.Title);
        if (titleControlInfo) {
            this.titlePlaceholder = titleControlInfo.watermark;
        }
    }

    public componentDidMount() {
        this.workItemEventService.subscribe(this.updateCommentCount, IWorkItemEventType.WorkItemCommentsUpdated);
        this.registerShortcuts();
        // Accessibility: handle case with first proper observer value loading
        if (this.workItem.infoText.value?.text) {
            announce(this.workItem.infoText.value.text, true);
        }
    }

    public componentWillUnmount(): void {
        this.workItemEventService.unsubscribe(this.updateCommentCount, IWorkItemEventType.WorkItemCommentsUpdated);
        this.unregisterShortcuts();
    }

    public getWorkItemSelfLink(): JSX.Element {
        return (
            <Observer dirty={this.workItem.dirty} revision={this.workItem.revision} workItemType={this.workItemType}>
                {() => {
                    const caption = this.workItem.getCaption({ excludeTempId: true, includeDirtyModifier: true }).toLocaleUpperCase();
                    const workItemUrl = this.getWorkItemUrl();

                    return (
                        <>
                            <WorkItemTypeIconProvider
                                dataProvider={this.workItemTrackingDataProvider}
                                dataSource={{ projectIdOrName: this.workItem.project.guid, workItemTypeName: this.workItem.getTypeData().name }}
                            >
                                {icon => <WorkItemTypeIcon data={icon} className="icon-margin font-size-m" />}
                            </WorkItemTypeIconProvider>
                            {this.workItem.isNew() || !workItemUrl ? (
                                caption
                            ) : (
                                <Link
                                    href={workItemUrl}
                                    onClick={e => {
                                        onClickFPS(this.context.pageContext, workItemUrl, true, e);
                                        this.props.onDismiss?.();
                                    }}
                                    removeUnderline={true}
                                >
                                    {caption}
                                </Link>
                            )}
                        </>
                    );
                }}
            </Observer>
        );
    }

    public getWorkItemIdDisplay(): JSX.Element {
        return (
            <Observer revision={this.workItem.revision}>
                {() => {
                    return this.workItem.isNew() ? null : this.workItem.getUniqueId();
                }}
            </Observer>
        );
    }

    public render(): JSX.Element {
        const subscribers = [
            <WorkItemFieldSubscriber
                key="wif-title-subscriber"
                fieldId={DalFields.TitleId}
                onUpdate={(value: string) => (this.workItemTitle.value = value)}
            />,
            <WorkItemFieldSubscriber
                key="wif-assignment-subscriber"
                fieldId={DalFields.AssignedToId}
                onUpdate={(value: IdentityRef) => this.updateIdentityPicker(value)}
            />,
            <WorkItemFieldSubscriber
                key="wif-wi-type-subscriber"
                fieldId={DalFields.WorkItemTypeId}
                onUpdate={() => (this.workItemType.value = this.workItem.getTypeData())}
            />
        ];

        return (
            <div className="flex-row">
                {subscribers}
                <Observer workItemType={this.workItemType}>
                    {(props: { workItemType: IWorkItemTypeData }) => (
                        <div className="work-item-form-header-type-deco" style={{ borderLeftColor: `#${props.workItemType.color ?? "FFFFFF"}` }} />
                    )}
                </Observer>
                <div className="work-item-form-header flex-column flex-grow padding-bottom-4 padding-left-8 h-scroll-auto">
                    <div className="flex-row flex-center secondary-text body-s padding-vertical-4 padding-right-4">
                        <div className="flex-row flex-noshrink">{this.getWorkItemSelfLink()}</div>
                        <Observer infoText={this.workItem.infoText}>
                            {(props: { infoText: IWorkItemInfoText }) => {
                                if (!props.infoText.text?.length) {
                                    return null;
                                }
                                // Accessibility: handle error announce for separated workitem fullscreen window
                                if (!this.isDialogFullscreen) announce(props.infoText.text, true);
                                return (
                                    <div
                                        className={css(
                                            "work-item-form-error-area flex-row flex-center font-size-m padding-left-8 text scroll-hidden",
                                            props.infoText.invalid && "error-text"
                                        )}
                                    >
                                        <Icon className="padding-right-4" iconName="AlertSolid" />
                                        <Tooltip overflowOnly={true}>
                                            <div className="text-ellipsis">{props.infoText.text}</div>
                                        </Tooltip>
                                    </div>
                                );
                            }}
                        </Observer>
                        <div className="flex-grow" />
                        {this.props.onToggleFullscreen && this.isDialogFullscreen && (
                            <Observer isDialogFullscreen={this.isDialogFullscreen}>
                                {(props: { isDialogFullscreen: boolean }) => {
                                    return (
                                        <Button
                                            ariaLabel={Resources.Fullscreen}
                                            iconProps={{ iconName: props.isDialogFullscreen ? "BackToWindow" : "FullScreen" }}
                                            onClick={() => {
                                                this.isDialogFullscreen.value = !props.isDialogFullscreen;
                                                this.props.onToggleFullscreen!();
                                            }}
                                            subtle
                                            tooltipProps={{ text: Resources.Fullscreen }}
                                        />
                                    );
                                }}
                            </Observer>
                        )}
                        {this.props.onDismiss && (
                            <Button
                                ariaLabel={Resources.Close}
                                iconProps={{ iconName: "ChromeClose" }}
                                onClick={() => this.props.onDismiss!()}
                                subtle
                                tooltipProps={{ text: Resources.Close }}
                            />
                        )}
                    </div>
                    <div className="flex-row body-xl padding-bottom-4 padding-right-8 flex-center">
                        {this.getWorkItemIdDisplay()}
                        <FocusOrMouseWithin>
                            {(status: IFocusOrMouseWithinStatus) => (
                                <div
                                    className="flex-row flex-grow flex-center"
                                    onBlur={status.onBlur}
                                    onFocus={status.onFocus}
                                    onMouseEnter={status.onMouseEnter}
                                    onMouseLeave={status.onMouseLeave}
                                >
                                    <TextField
                                        ariaLabel={Resources.TitleFieldAriaLabel}
                                        autoFocus={this.workItem.isNew()}
                                        className={css("work-item-title-textfield", !status.hasFocus && !status.hasMouse ? "not-active" : undefined)}
                                        containerClassName="flex-grow padding-left-8"
                                        onChange={(ev, value) => {
                                            this.workItemTitle.value = value;
                                            this.workItem.setFieldValueById(DalFields.TitleId, value);
                                            if (isNewWorkItemFormRoute(this.context.pageContext) && !this.props.onDismiss) {
                                                document.title = getWorkItemFormTitle(this.workItem);
                                            }
                                        }}
                                        placeholder={this.titlePlaceholder}
                                        readOnly={isReadOnly(this.workItem.getFieldById(DalFields.TitleId)!)}
                                        value={this.workItemTitle}
                                    />
                                    <ClipboardButton
                                        className={css(!status.hasFocus && !status.hasMouse && "invisible", this.workItem.isNew() && "hidden")}
                                        getContent={this.getWorkItemFormLinkToCopy}
                                    />
                                </div>
                            )}
                        </FocusOrMouseWithin>
                    </div>
                    <div>
                        <WorkItemFormHeaderCommandBar
                            onDelete={this.props.onDelete}
                            onDismiss={this.props.onDismiss}
                            onRefreshed={this.props.onRefreshed}
                            onReset={this.props.onReset}
                            onSave={this.props.onSave}
                            currentIterationPath={this.props.currentIterationPath}
                        />
                        <div className="inline-flex-row">
                            <ConditionalChildren renderChildren={this.assignedIdentityResolved}>
                                <Observer assignment={this.workItemAssignment}>
                                    {() => {
                                        const assignedToField = this.workItem.getFieldById(DalFields.AssignedToId)!;
                                        return (
                                            <IdentityPickerDropdown
                                                ariaLabel={assignedToField.fieldDefinition.name}
                                                className="work-item-assignment-dropdown"
                                                disabled={isReadOnly(assignedToField)}
                                                onChange={identity => {
                                                    this.workItemAssignment.value = identity;
                                                    this.workItem.setFieldValueById(
                                                        DalFields.AssignedToId,
                                                        identity
                                                            ? convertIIdentityToWorkItemIdentityRef(this.pageContext, identity, identity.image)
                                                            : undefined,
                                                        {
                                                            fireIdentityEagerValidation: true
                                                        }
                                                    );
                                                }}
                                                pickerProvider={this.identityPickerProvider}
                                                value={this.workItemAssignment}
                                            />
                                        );
                                    }}
                                </Observer>
                            </ConditionalChildren>
                        </div>
                        <Link
                            className="wif-comment-count-link inline-flex-row flex-center flex-noshrink padding-horizontal-8"
                            onClick={this.props.onCommentCountClick}
                            removeUnderline={true}
                        >
                            <Icon iconName="Comment" />
                            &nbsp;
                            <Observer commentCount={this.commentCount}>
                                {(props: { commentCount: number }) => (
                                    <div className="wif-comment-count-link-text">
                                        {props.commentCount === 1 ? Resources.OneComment : format(Resources.CommentCount, props.commentCount)}
                                    </div>
                                )}
                            </Observer>
                        </Link>
                        <WorkItemTagPicker includeFocusZone />
                    </div>
                </div>
            </div>
        );
    }

    private async updateIdentityPicker(fieldValue: any): Promise<void> {
        const allowedValues = await this.workItemFieldService.getAllowedValues(
            CoreFieldRefNames.AssignedTo,
            this.workItem.project.guid,
            this.workItem.workItemType.name
        );

        if (this.identityPickerProvider.setAdditionalEntries && allowedValues) {
            this.identityPickerProvider.setAdditionalEntries(allowedValues);
        }

        if (fieldValue) {
            const identity = convertPotentialIdentityRefFromFieldValue(this.pageContext, fieldValue, true) as IdentityRef;
            const entity = identity.id
                ? await (this.identityPickerProvider.getEntityFromUniqueAttribute(identity.id) as PromiseLike<IIdentity>)
                : this.getIdentityFromInvalidIdentityRef(identity);
            this.workItemAssignment.value = entity;
            this.assignedIdentityResolved.value = true;
        } else {
            this.workItemAssignment.value = undefined;
            this.assignedIdentityResolved.value = true;
        }
    }

    /**
     * We still want to show the Assigned To identity even if it doesn't exist
     * so the user can fix easily identify and fix the problem
     */
    private getIdentityFromInvalidIdentityRef(identity: IdentityRef): IIdentity {
        return {
            entityId: identity.id,
            displayName: identity.displayName,
            image: identity.imageUrl
        } as IIdentity;
    }

    private getWorkItemUrl(): string | undefined {
        if (this.workItem.isNew()) {
            return undefined;
        } else {
            const project = this.workItem.project.name;
            return WorkItemUtils.getWorkItemUrl(this.pageContext, this.workItem.id, project);
        }
    }

    private getWorkItemFormLinkToCopy = (): JSX.Element => {
        return (
            <>
                <Link href={this.getWorkItemUrl()}>{this.workItem.getCaption({ excludeTempId: true })}</Link>
                {`: ${this.workItemTitle.value}`}
            </>
        );
    };

    private updateCommentCount = (): void => {
        const commentsCount = this.workItemFormCommentService.getTotalCommentsInWorkItem(this.workItem);
        this.commentCount.value = commentsCount || this.workItem.getFieldValueById(DalFields.CommentCountId);
    };

    private registerShortcuts = (): void => {
        const shortcutService = this.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");

        shortcutService.registerShortcut(this.props.shortcutGroupName, {
            combos: ["alt+i"],
            description: Resources.AssignWorkItemToMeShortcut,
            globalCombos: ["alt+i"],
            action: () => {
                const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
                if (pageData) {
                    const workItemIdentityRef: WorkItemIdentityRef = {
                        distinctDisplayName: getDistinctDisplayName(pageData.user.displayName, pageData.user.uniqueName),
                        identityRef: pageData.user
                    };
                    const identity = convertWorkItemIdentityRefToIIdentity(this.pageContext, workItemIdentityRef);
                    if (identity) {
                        this.workItemAssignment.value = identity;
                        this.workItem.setFieldValueById(DalFields.AssignedToId, workItemIdentityRef);
                    }
                }
            }
        });

        shortcutService.registerShortcut(this.props.shortcutGroupName, {
            combos: ["shift+alt+c"],
            description: Resources.CopyWorkItemTitleShortcut,
            globalCombos: ["shift+alt+c"],
            action: () => {
                copyToClipboard(this.getWorkItemFormLinkToCopy());
            }
        });
    };

    private unregisterShortcuts = (): void => {
        const shortcutService = this.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.unRegisterShortcut(this.props.shortcutGroupName, "alt+i");
        shortcutService.unRegisterShortcut(this.props.shortcutGroupName, "shift+alt+c");
    };
}
